import { selectors } from 'modules/common';
import { backend } from 'modules/data';
import { useMemo } from 'react';
import { useCompanyBetaFeatures } from 'shared/hooks';

import { IOffboardingContext, useOffboardingContext } from '../../OffboardingContext';
import {
    getRoleDisplayName,
    getWorkflowAutoApproveStepId,
    getWorkflowReplacementId,
    isApproverRoleRequired,
    isRequiredRoleToOffboard,
} from '../../OffboardUserPopupContent.helpers';
import { Row } from '../../OffboardUserPopupContent.types';
import { messages } from './ApprovalWorkflows.messages';
import { ApprovalWorkflowsProps } from './ApprovalWorkflows.types';

export const useApprovalWorkflowsData = (
    companyId: ApprovalWorkflowsProps['companyId'],
    workflows: ApprovalWorkflowsProps['workflows'] = []
) => {
    const { selectedReplacements } = useOffboardingContext();

    const { isPayerOffboarding } = useCompanyBetaFeatures(companyId);

    return useMemo(
        () => mapToTableRows(companyId, workflows, selectedReplacements, isPayerOffboarding),
        [companyId, workflows, selectedReplacements, isPayerOffboarding]
    );
};

const mapToTableRows = (
    companyId: ApprovalWorkflowsProps['companyId'],
    workflows: ApprovalWorkflowsProps['workflows'] = [],
    selectedReplacements: IOffboardingContext['selectedReplacements'],
    isPayerOffboardingBetaFeature: boolean
): Row[] => {
    return workflows.flatMap((workflow) => {
        const requiredApprovedRole = isApproverRoleRequired(workflow, selectedReplacements);

        const tableRows = workflow.replacements.map((replacement) => {
            const replacementId = getWorkflowReplacementId(replacement, workflow);

            let required = false;

            if (isPayerOffboardingBetaFeature) {
                required = replacement.isReplacementRequired;
            } else if (isRequiredRoleToOffboard(replacement.role)) {
                required = !selectedReplacements[replacementId];
            } else if (replacement.role === backend.ParticipantRole.Approver) {
                required = requiredApprovedRole;
            }

            // Some properties from Row interface is not passed so the `as Row` casting is needed
            return {
                id: getWorkflowReplacementId(replacement, workflow),
                workflowId: workflow.workflowId,
                name: workflow.name || selectors.template.getTemplateDisplayNameByCode(workflow.integrationCode),
                role: getRoleDisplayName(replacement.role),
                step: replacement.stepName,
                required,
                companyId,
            } as unknown as Row;
        });

        if (workflow.hasRequesterInAutoApprovalRules) {
            const replacementId = getWorkflowAutoApproveStepId(workflow);
            const requiredReplacementFilled = selectedReplacements[replacementId];

            // Some properties from Row interface is not passed so the `as Row` casting is needed
            tableRows.push({
                id: replacementId,
                workflowId: workflow.workflowId,
                name: workflow.name || selectors.template.getTemplateDisplayNameByCode(workflow.integrationCode),
                role: getRoleDisplayName(backend.ParticipantRole.Requester),
                step: messages.autoApprovalStep,
                required: !requiredReplacementFilled,
                companyId,
            } as unknown as Row);
        }

        return tableRows;
    });
};
